import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    SignOut,
    MagnifyingGlass,
    Trash,
    CaretDown,
    CaretUp,
    EnvelopeSimple,
    Phone,
    Globe,
    Calendar,
    DownloadSimple,
    ChartBar,
    Trophy,
    TrendUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../lib/AuthContext";
import { PRICE_INDEX, formatCAD, PAGES, FEATURES, ASSETS } from "../data/briefCatalog";
import SEO from "../components/SEO";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_brand-builders-43/artifacts/6hieg182_Artboard%2010.svg";

const PAGE_LABELS = Object.fromEntries(PAGES.map((p) => [p.id, p.label]));
const FEATURE_LABELS = Object.fromEntries(FEATURES.map((f) => [f.id, f.label]));
const ASSET_LABELS = Object.fromEntries(ASSETS.map((a) => [a.id, a.label]));

export default function AdminBriefs() {
    const { user, loading, logout, api, setUser } = useAuth();
    const navigate = useNavigate();
    const [briefs, setBriefs] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [query, setQuery] = useState("");
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        if (loading || !user) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/admin/briefs");
                if (!cancelled) setBriefs(data);
            } catch (err) {
                if (err?.response?.status === 401) {
                    setUser(null);
                    navigate("/admin");
                    return;
                }
                toast.error(
                    formatApiErrorDetail(err?.response?.data?.detail) ||
                        "Impossible de charger les briefs.",
                );
            } finally {
                if (!cancelled) setLoadingList(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loading, user, api, navigate, setUser]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return briefs;
        return briefs.filter((b) =>
            [b.reference, b.full_name, b.company, b.email, b.site_type, b.priority]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q)),
        );
    }, [query, briefs]);

    const totalPipeline = useMemo(
        () => briefs.reduce((sum, b) => sum + (b.estimated_total || 0), 0),
        [briefs],
    );

    const onLogout = async () => {
        await logout();
        navigate("/admin");
    };

    const onDelete = async (id) => {
        if (!window.confirm("Supprimer ce brief définitivement ?")) return;
        try {
            await api.delete(`/admin/briefs/${id}`);
            setBriefs((prev) => prev.filter((b) => b.id !== id));
            toast.success("Brief supprimé.");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Erreur lors de la suppression.",
            );
        }
    };

    const onExportCsv = async () => {
        try {
            const response = await api.get("/admin/briefs.csv", {
                responseType: "blob",
            });
            const blob = new Blob([response.data], {
                type: "text/csv;charset=utf-8",
            });
            const cd = response.headers?.["content-disposition"] || "";
            const match = cd.match(/filename="?([^";]+)"?/i);
            const filename =
                match?.[1] ||
                `studio-cent-douze-briefs-${new Date().toISOString().slice(0, 10)}.csv`;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Export CSV téléchargé.");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Échec de l'export CSV.",
            );
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-scd text-cream">
                <span className="label-eyebrow text-cream/60">Chargement…</span>
            </div>
        );
    }
    if (!user) return <Navigate to="/admin" replace />;

    return (
        <main
            data-testid="admin-briefs-page"
            className="min-h-screen bg-scd text-cream"
        >
            <SEO title="Briefs clients (admin)" path="/admin/briefs" noindex={true} />
            {/* Header */}
            <header className="border-b border-scd bg-[#faf7f1]/90 backdrop-blur-xl sticky top-0 z-40">
                <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4">
                    <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
                        <img
                            src={LOGO_URL}
                            alt="Studio Cent Douze"
                            className="h-8 w-auto"
                        />
                        <span className="hidden sm:inline label-eyebrow text-yellow">
                            Admin · Briefs
                        </span>
                    </a>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin/analytics"
                            className="hidden sm:inline label-eyebrow text-cream/70 hover:text-yellow"
                            data-testid="admin-analytics-link"
                        >
                            Analytics →
                        </Link>
                        <Link
                            to="/admin/blog"
                            className="hidden sm:inline label-eyebrow text-cream/70 hover:text-yellow"
                            data-testid="admin-blog-link"
                        >
                            Journal →
                        </Link>
                        <Link
                            to="/admin/showreel"
                            className="hidden sm:inline label-eyebrow text-cream/70 hover:text-yellow"
                            data-testid="admin-showreel-link"
                        >
                            Nos réalisations →
                        </Link>
                        <span className="hidden md:inline text-sm text-cream/70">
                            {user.email}
                        </span>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout-button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-scd text-cream hover:border-yellow hover:text-yellow text-sm transition-colors"
                        >
                            <SignOut size={16} weight="bold" />
                            Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <section className="px-6 md:px-10 py-10 max-w-7xl mx-auto">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
                    <Stat
                        label="Briefs reçus"
                        value={briefs.length.toString()}
                        testId="admin-stat-total"
                    />
                    <Stat
                        label="Pipeline estimé"
                        value={formatCAD(totalPipeline)}
                        testId="admin-stat-pipeline"
                    />
                    <Stat
                        label="Dernier brief"
                        value={
                            briefs.length > 0
                                ? new Date(briefs[0].created_at).toLocaleDateString("fr-CA")
                                : "—"
                        }
                        testId="admin-stat-last"
                    />
                </div>

                {/* Acquisition dashboard */}
                <AcquisitionDashboard briefs={briefs} />

                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <h1 className="font-display text-3xl md:text-4xl">
                        Briefs clients
                    </h1>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="relative flex-1 sm:w-80">
                            <MagnifyingGlass
                                size={16}
                                className="absolute top-1/2 -translate-y-1/2 left-3 text-cream/50"
                            />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Rechercher (référence, nom, entreprise…)"
                                data-testid="admin-search-input"
                                className="w-full bg-elev border border-scd rounded-sm pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-yellow"
                            />
                        </div>
                        <button
                            onClick={onExportCsv}
                            disabled={briefs.length === 0}
                            data-testid="admin-export-csv-button"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-scd text-cream hover:border-yellow hover:text-yellow text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            <DownloadSimple size={16} weight="bold" />
                            Exporter CSV
                        </button>
                    </div>
                </div>

                {/* Table */}
                {loadingList ? (
                    <div className="text-center py-16 text-cream/60 label-eyebrow">
                        Chargement des briefs…
                    </div>
                ) : filtered.length === 0 ? (
                    <div
                        className="text-center py-16 border border-scd bg-elev rounded-sm"
                        data-testid="admin-empty-state"
                    >
                        <p className="text-cream/70">
                            {briefs.length === 0
                                ? "Aucun brief reçu pour l'instant."
                                : "Aucun résultat pour cette recherche."}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filtered.map((b) => (
                            <BriefRow
                                key={b.id}
                                brief={b}
                                open={openId === b.id}
                                onToggle={() =>
                                    setOpenId(openId === b.id ? null : b.id)
                                }
                                onDelete={() => onDelete(b.id)}
                            />
                        ))}
                    </div>
                )}
            </section>
        </main>
    );
}

function Stat({ label, value, testId }) {
    return (
        <div className="border border-scd bg-elev rounded-sm p-5" data-testid={testId}>
            <span className="label-eyebrow text-cream/60">{label}</span>
            <div className="font-display text-3xl text-yellow mt-2">{value}</div>
        </div>
    );
}

function BriefRow({ brief, open, onToggle, onDelete }) {
    const itemNames = brief.selected_items
        .map((id) => PRICE_INDEX[id]?.label)
        .filter(Boolean);

    return (
        <motion.div
            layout
            data-testid={`admin-brief-row-${brief.reference}`}
            className="border border-scd bg-elev rounded-sm overflow-hidden"
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full grid grid-cols-12 gap-4 px-5 py-4 items-center text-left hover:bg-[#3a3a3a] transition-colors"
            >
                <div className="col-span-12 sm:col-span-3">
                    <div className="label-eyebrow text-yellow">{brief.reference}</div>
                    <div className="text-sm text-cream/65 mt-1">
                        {new Date(brief.created_at).toLocaleString("fr-CA")}
                    </div>
                </div>
                <div className="col-span-12 sm:col-span-4">
                    <div className="font-display text-lg text-cream leading-tight">
                        {brief.company}
                    </div>
                    <div className="text-sm text-cream/65">{brief.full_name}</div>
                </div>
                <div className="col-span-7 sm:col-span-3 text-sm text-cream/85 truncate">
                    <div className="truncate">
                        {brief.site_type || "—"}
                        {brief.priority ? (
                            <span className="text-cream/50"> · {brief.priority}</span>
                        ) : null}
                    </div>
                    <SourceBadge brief={brief} />
                </div>
                <div className="col-span-5 sm:col-span-2 text-right">
                    <div className="font-display text-xl text-yellow">
                        {formatCAD(brief.estimated_total || 0)}
                    </div>
                    <div className="text-xs text-cream/50 inline-flex items-center justify-end gap-1 mt-1">
                        {open ? (
                            <CaretUp size={12} weight="bold" />
                        ) : (
                            <CaretDown size={12} weight="bold" />
                        )}
                        Détails
                    </div>
                </div>
            </button>

            {open && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.25 }}
                    className="border-t border-scd px-5 py-6 bg-scd/40 grid lg:grid-cols-3 gap-8"
                >
                    {/* Contact + meta */}
                    <div className="space-y-3 text-sm">
                        <h4 className="label-eyebrow text-yellow">Contact</h4>
                        <a
                            href={`mailto:${brief.email}`}
                            className="flex items-center gap-2 text-cream hover:text-yellow"
                        >
                            <EnvelopeSimple size={14} /> {brief.email}
                        </a>
                        {brief.phone && (
                            <div className="flex items-center gap-2 text-cream">
                                <Phone size={14} /> {brief.phone}
                            </div>
                        )}
                        {brief.current_website && (
                            <a
                                href={brief.current_website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-cream hover:text-yellow break-all"
                            >
                                <Globe size={14} /> {brief.current_website}
                            </a>
                        )}
                        {brief.launch_date && (
                            <div className="flex items-center gap-2 text-cream">
                                <Calendar size={14} /> Lancement: {brief.launch_date}
                            </div>
                        )}
                        {brief.sector && (
                            <div className="text-cream/70">
                                Secteur: <span className="text-cream">{brief.sector}</span>
                            </div>
                        )}
                    </div>

                    {/* Project */}
                    <div className="space-y-3 text-sm">
                        <h4 className="label-eyebrow text-yellow">Projet</h4>
                        {brief.one_liner && (
                            <p className="text-cream/85 italic">"{brief.one_liner}"</p>
                        )}
                        {brief.main_goal && (
                            <Block label="Objectif">{brief.main_goal}</Block>
                        )}
                        {brief.target_audience && (
                            <Block label="Public cible">{brief.target_audience}</Block>
                        )}
                        {brief.mood && (
                            <div className="text-cream/85">
                                Ambiance: <span className="text-yellow">{brief.mood}</span>
                            </div>
                        )}
                        {brief.tone && (
                            <div className="text-cream/85">
                                Ton: <span className="text-yellow">{brief.tone}</span>
                            </div>
                        )}
                        {brief.references && (
                            <Block label="Références">{brief.references}</Block>
                        )}
                        {brief.avoid && <Block label="À éviter">{brief.avoid}</Block>}
                        {brief.additional_info && (
                            <Block label="Notes">{brief.additional_info}</Block>
                        )}

                        <AttributionBlock brief={brief} />
                    </div>

                    {/* Items + total */}
                    <div className="space-y-4 text-sm">
                        <h4 className="label-eyebrow text-yellow">Estimation</h4>
                        {itemNames.length === 0 ? (
                            <p className="text-cream/55">Aucun item sélectionné.</p>
                        ) : (
                            <ul className="space-y-1.5">
                                {brief.selected_items.map((id) => {
                                    const it = PRICE_INDEX[id];
                                    if (!it) return null;
                                    return (
                                        <li
                                            key={id}
                                            className="flex justify-between gap-3 text-cream/85"
                                        >
                                            <span>{it.label}</span>
                                            <span className="text-yellow font-medium">
                                                {formatCAD(it.price)}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        <div className="pt-3 border-t border-scd flex justify-between items-baseline">
                            <span className="label-eyebrow text-cream/60">Total</span>
                            <span className="font-display text-2xl text-yellow">
                                {formatCAD(brief.estimated_total || 0)}
                            </span>
                        </div>
                        {brief.client_estimated_total != null &&
                            Math.abs(
                                (brief.client_estimated_total || 0) -
                                    (brief.estimated_total || 0),
                            ) > 0.5 && (
                                <p className="text-xs text-[#ff9c5b]">
                                    ⚠ Total client envoyé:{" "}
                                    {formatCAD(brief.client_estimated_total)} (recalculé
                                    serveur).
                                </p>
                            )}

                        {brief.pages?.length > 0 && (
                            <Tags label="Pages" values={brief.pages} dict={PAGE_LABELS} />
                        )}
                        {brief.features?.length > 0 && (
                            <Tags
                                label="Fonctionnalités"
                                values={brief.features}
                                dict={FEATURE_LABELS}
                            />
                        )}
                        {brief.assets?.length > 0 && (
                            <Tags
                                label="Assets fournis"
                                values={brief.assets}
                                dict={ASSET_LABELS}
                            />
                        )}

                        <div className="pt-4 flex justify-end">
                            <button
                                onClick={onDelete}
                                data-testid={`admin-delete-${brief.reference}`}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-[#ff6b6b]/40 text-[#ff6b6b] hover:bg-[#ff6b6b]/10 text-xs"
                            >
                                <Trash size={14} /> Supprimer
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
}

function Block({ label, children }) {
    return (
        <div>
            <span className="label-eyebrow text-cream/55">{label}</span>
            <p className="text-cream/85 mt-1 whitespace-pre-wrap">{children}</p>
        </div>
    );
}

function Tags({ label, values, dict }) {
    return (
        <div>
            <span className="label-eyebrow text-cream/55 block mb-2">{label}</span>
            <div className="flex flex-wrap gap-1.5">
                {values.map((v) => (
                    <span
                        key={v}
                        className="px-2 py-1 rounded-sm bg-[var(--scd-yellow-tint)] text-green text-xs"
                    >
                        {dict[v] || v}
                    </span>
                ))}
            </div>
        </div>
    );
}

// ------------------------------------------------------------
//              Marketing attribution helpers
// ------------------------------------------------------------
function deriveSource(brief) {
    // Returns { source, medium, color } where color is a Tailwind class fragment.
    if (brief.gclid) return { source: "google", medium: "cpc (Google Ads)", tone: "yellow" };
    if (brief.fbclid) return { source: "meta", medium: "cpc (Facebook / Instagram Ads)", tone: "green" };
    if (brief.utm_source) {
        return {
            source: brief.utm_source,
            medium: brief.utm_medium || "referral",
            tone: "yellow",
        };
    }
    if (brief.referrer) {
        try {
            const host = new URL(brief.referrer).hostname.replace(/^www\./, "");
            return { source: host, medium: "referral", tone: "green" };
        } catch {
            return { source: brief.referrer.slice(0, 40), medium: "referral", tone: "green" };
        }
    }
    return { source: "direct", medium: "none", tone: "neutral" };
}

function SourceBadge({ brief }) {
    const { source, medium, tone } = deriveSource(brief);
    const cls =
        tone === "yellow"
            ? "bg-[var(--scd-yellow-tint)] text-green border-[var(--scd-yellow)]/40"
            : tone === "green"
            ? "bg-green/15 text-green border-green/40"
            : "bg-cream/5 text-cream/60 border-cream/15";
    return (
        <span
            data-testid={`admin-brief-source-${brief.reference}`}
            className={`mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide font-medium ${cls}`}
            title={`Source: ${source} · Medium: ${medium}`}
        >
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            {source}
            {medium && medium !== "none" && (
                <span className="opacity-60 normal-case tracking-normal">
                    · {medium}
                </span>
            )}
        </span>
    );
}

function AttributionBlock({ brief }) {
    const rows = [
        ["utm_source", brief.utm_source],
        ["utm_medium", brief.utm_medium],
        ["utm_campaign", brief.utm_campaign],
        ["utm_term", brief.utm_term],
        ["utm_content", brief.utm_content],
        ["gclid", brief.gclid],
        ["fbclid", brief.fbclid],
        ["referrer", brief.referrer],
        ["landing_page", brief.landing_page],
        ["first_seen_at", brief.first_seen_at],
    ].filter(([, v]) => v);

    const { source, medium } = deriveSource(brief);

    return (
        <div className="mt-4 pt-4 border-t border-scd">
            <h4 className="label-eyebrow text-yellow mb-3">Provenance</h4>
            <div className="text-cream/85 text-sm">
                <span className="text-cream">{source}</span>
                <span className="text-cream/50"> · {medium}</span>
            </div>
            {rows.length > 0 ? (
                <dl className="mt-3 grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-xs">
                    {rows.map(([k, v]) => (
                        <React.Fragment key={k}>
                            <dt className="text-cream/50 font-mono">{k}</dt>
                            <dd className="text-cream/85 break-all">{v}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            ) : (
                <p className="text-xs text-cream/50 mt-2">
                    Aucun paramètre UTM ni référent tiers capturé (visite directe).
                </p>
            )}
        </div>
    );
}


// ------------------------------------------------------------
//              Acquisition dashboard (mini analytics)
// ------------------------------------------------------------
function aggregateBySource(briefs) {
    const buckets = new Map();
    for (const b of briefs) {
        const { source } = deriveSource(b);
        const key = source || "direct";
        const bucket = buckets.get(key) || {
            source: key,
            count: 0,
            pipeline: 0,
            withPipeline: 0,
            campaigns: new Set(),
        };
        bucket.count += 1;
        const val = Number(b.estimated_total) || 0;
        bucket.pipeline += val;
        if (val > 0) bucket.withPipeline += 1;
        if (b.utm_campaign) bucket.campaigns.add(b.utm_campaign);
        buckets.set(key, bucket);
    }
    return Array.from(buckets.values()).sort((a, b) => b.pipeline - a.pipeline);
}

function aggregateByCampaign(briefs) {
    const buckets = new Map();
    for (const b of briefs) {
        if (!b.utm_campaign) continue;
        const key = b.utm_campaign;
        const bucket = buckets.get(key) || {
            campaign: key,
            source: b.utm_source || "—",
            count: 0,
            pipeline: 0,
        };
        bucket.count += 1;
        bucket.pipeline += Number(b.estimated_total) || 0;
        buckets.set(key, bucket);
    }
    return Array.from(buckets.values()).sort((a, b) => b.pipeline - a.pipeline);
}

function AcquisitionDashboard({ briefs }) {
    const stats = useMemo(() => {
        if (!briefs || briefs.length === 0) return null;

        const bySource = aggregateBySource(briefs);
        const byCampaign = aggregateByCampaign(briefs);

        const totalLeads = briefs.length;
        const totalPipeline = briefs.reduce(
            (s, b) => s + (Number(b.estimated_total) || 0),
            0,
        );

        // Trend: last 7 vs previous 7 days
        const now = Date.now();
        const day = 24 * 60 * 60 * 1000;
        const last7 = briefs.filter(
            (b) => now - new Date(b.created_at).getTime() < 7 * day,
        ).length;
        const prev7 = briefs.filter((b) => {
            const t = now - new Date(b.created_at).getTime();
            return t >= 7 * day && t < 14 * day;
        }).length;

        const topSource = bySource[0];
        const topCampaign = byCampaign[0];
        const attributedLeads = bySource
            .filter((s) => s.source !== "direct")
            .reduce((n, s) => n + s.count, 0);
        const attributionRate = totalLeads > 0 ? (attributedLeads / totalLeads) * 100 : 0;

        return {
            bySource: bySource.slice(0, 8),
            byCampaign: byCampaign.slice(0, 5),
            totalLeads,
            totalPipeline,
            last7,
            prev7,
            trend: last7 - prev7,
            topSource,
            topCampaign,
            attributionRate,
            maxPipeline: Math.max(...bySource.map((s) => s.pipeline), 1),
        };
    }, [briefs]);

    if (!stats) return null;

    return (
        <div
            data-testid="admin-acquisition-dashboard"
            className="mt-10 mb-12"
        >
            <div className="flex items-baseline justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                    <ChartBar size={20} weight="duotone" className="text-yellow" />
                    <h2 className="font-display text-2xl md:text-3xl text-cream">
                        Acquisition
                    </h2>
                </div>
                <span className="text-xs text-cream/50">
                    D&apos;où viennent tes leads · dernier 7 j : {stats.last7}
                    {stats.trend !== 0 && (
                        <span
                            className={
                                stats.trend > 0 ? "text-green ml-1" : "text-[#ff9c5b] ml-1"
                            }
                        >
                            ({stats.trend > 0 ? "+" : ""}
                            {stats.trend} vs. semaine préc.)
                        </span>
                    )}
                </span>
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-6">
                <MiniKPI
                    icon={<Trophy size={16} weight="duotone" className="text-yellow" />}
                    label="Source #1"
                    value={stats.topSource?.source || "—"}
                    sub={
                        stats.topSource
                            ? `${stats.topSource.count} briefs · ${formatCAD(
                                  stats.topSource.pipeline,
                              )}`
                            : ""
                    }
                    testId="admin-kpi-top-source"
                />
                <MiniKPI
                    icon={
                        <ChartBar size={16} weight="duotone" className="text-yellow" />
                    }
                    label="Taux d'attribution"
                    value={`${stats.attributionRate.toFixed(0)} %`}
                    sub={`${stats.totalLeads - Math.round((stats.attributionRate * stats.totalLeads) / 100)} en visite directe`}
                    testId="admin-kpi-attribution-rate"
                />
                <MiniKPI
                    icon={
                        <TrendUp size={16} weight="duotone" className="text-yellow" />
                    }
                    label="Top campagne"
                    value={stats.topCampaign?.campaign || "—"}
                    sub={
                        stats.topCampaign
                            ? `${stats.topCampaign.source} · ${formatCAD(stats.topCampaign.pipeline)}`
                            : "Ajoute des paramètres utm_campaign à tes liens"
                    }
                    testId="admin-kpi-top-campaign"
                />
            </div>

            {/* Source breakdown table */}
            <div className="border border-scd bg-elev rounded-sm overflow-hidden">
                <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-scd text-[11px] uppercase tracking-wider text-cream/50">
                    <div className="col-span-4">Source</div>
                    <div className="col-span-1 text-right">Briefs</div>
                    <div className="col-span-1 text-right">%</div>
                    <div className="col-span-3 text-right">Pipeline estimé</div>
                    <div className="col-span-3 text-right">Ticket moyen</div>
                </div>
                {stats.bySource.map((s) => {
                    const share = (s.count / stats.totalLeads) * 100;
                    const avg = s.count > 0 ? s.pipeline / s.count : 0;
                    const barPct = (s.pipeline / stats.maxPipeline) * 100;
                    const isDirect = s.source === "direct";
                    return (
                        <div
                            key={s.source}
                            data-testid={`admin-acq-row-${s.source}`}
                            className="relative grid grid-cols-12 gap-4 px-5 py-3.5 items-center border-b border-scd/60 last:border-b-0"
                        >
                            {/* Bar background */}
                            <div
                                className="absolute inset-y-0 left-0 pointer-events-none"
                                style={{
                                    width: `${barPct}%`,
                                    background: isDirect
                                        ? "linear-gradient(90deg, rgba(39,39,39,0.05), transparent)"
                                        : "linear-gradient(90deg, rgba(255,209,64,0.16), transparent)",
                                }}
                            />
                            <div className="col-span-4 flex items-center gap-2 relative">
                                <span
                                    className={`w-1.5 h-1.5 rounded-full ${
                                        isDirect ? "bg-cream/40" : "bg-yellow"
                                    }`}
                                />
                                <span className="text-cream font-medium capitalize">
                                    {s.source}
                                </span>
                                {s.campaigns.size > 0 && (
                                    <span className="text-[10px] text-cream/50">
                                        {s.campaigns.size} campagne
                                        {s.campaigns.size > 1 ? "s" : ""}
                                    </span>
                                )}
                            </div>
                            <div className="col-span-1 text-right text-cream/85 relative">
                                {s.count}
                            </div>
                            <div className="col-span-1 text-right text-cream/60 text-sm relative">
                                {share.toFixed(0)} %
                            </div>
                            <div className="col-span-3 text-right text-yellow font-display relative">
                                {formatCAD(s.pipeline)}
                            </div>
                            <div className="col-span-3 text-right text-cream/70 text-sm relative">
                                {avg > 0 ? formatCAD(avg) : "—"}
                            </div>
                        </div>
                    );
                })}
            </div>

            {stats.byCampaign.length === 0 && (
                <p className="text-xs text-cream/50 mt-3">
                    Astuce : ajoute <code className="text-yellow">?utm_source=…&amp;utm_campaign=…</code>
                    {" "}à tes liens Instagram / LinkedIn / newsletters pour tracker
                    précisément quelles campagnes rapportent le plus.
                </p>
            )}
        </div>
    );
}

function MiniKPI({ icon, label, value, sub, testId }) {
    return (
        <div
            data-testid={testId}
            className="border border-scd bg-elev rounded-sm p-4"
        >
            <div className="flex items-center gap-2 text-cream/60">
                {icon}
                <span className="label-eyebrow">{label}</span>
            </div>
            <div className="mt-2 font-display text-xl md:text-2xl text-cream capitalize truncate">
                {value}
            </div>
            {sub && (
                <div className="text-xs text-cream/55 mt-1 truncate">{sub}</div>
            )}
        </div>
    );
}

